import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { selectIsRokuTemplatingFeatureFlagOn } from 'core/userContext/selectors';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { useLocalesQueryContext } from 'modules/campaigns/lib/contexts/LocalesQueryContext';
import { TemplateAndContent } from 'modules/graphql';
import { getCorrectlyCasedLocale } from '../common/utils';
import { useCampaignQueryContext } from '../context/CampaignQueryContext';
import { useQueryParamContext } from '../context/QueryParamContext';
import { useTemplateQueryContext } from '../context/TemplateQueryContext';
import { useFetchDefaultLocaleContent } from './useFetchDefaultLocaleContent';
import { useSaveTemplate } from './useSaveTemplate';
import { FetchTemplateSingleContentByIdQueryResponse } from './useTemplateSingleContentEditById';
import { prepInitialData } from './utils';

export const useAddLocale = () => {
  const { saveTemplateLocale } = useSaveTemplate();
  const dispatch = useDispatch();
  const { templateId, setLocale } = useQueryParamContext();
  const { medium } = useTemplateQueryContext();
  const { getDefaultLocaleContent, isLoading } = useFetchDefaultLocaleContent();
  const {
    allProjectLocales: projectLocales,
    refetch: refetchLocalesForLocaleSwitcher,
    isLoading: isLoadingLocales,
  } = useLocalesQueryContext();
  const { campaign } = useCampaignQueryContext();

  const isRokuTemplatingOn = useSelector(selectIsRokuTemplatingFeatureFlagOn);

  const handleAddLocale = useCallback(
    (localeTag: string) => {
      if (!isLoadingLocales && !isLoading) {
        const onCompleted = (resp: FetchTemplateSingleContentByIdQueryResponse) => {
          const responseData = resp.templateAndSingleContent || {};
          const { primaryContent, ...commonFields } = responseData as TemplateAndContent;
          const defaultLocaleContentObj = JSON.parse(
            prepInitialData(
              medium,
              commonFields,
              primaryContent ?? {},
              campaign,
              isRokuTemplatingOn,
              true,
            ),
          );
          // modify the defaultLocaleContent to have the specified locale
          defaultLocaleContentObj.mediumData.locale = {
            name: getCorrectlyCasedLocale(localeTag, projectLocales),
            __typename: 'TemplateLocale',
          };

          // save the new locale content

          saveTemplateLocale(defaultLocaleContentObj, () => {
            refetchLocalesForLocaleSwitcher();
            dispatch(addPositiveToast(`${localeTag} version created successfully`));
            setLocale(localeTag);
            // template refetch is not necessary because the change in locale will trigger the refetch
          });
        };
        const onError = () => {
          dispatch(addNegativeToast(`Unable to add ${localeTag} locale`));
        };
        getDefaultLocaleContent({
          templateId,
          onCompleted,
          onError,
        });
      }
    },
    [medium, templateId, projectLocales, isLoading, isLoadingLocales],
  );
  return { addLocale: handleAddLocale, isAdding: isLoading };
};
